/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable security/detect-object-injection */
/* eslint-disable max-lines-per-function */
import { z } from 'zod';

import { GoodsLocationType, SortDirection } from '@e-origin/shared';

const ADDRESS_COUNTRY_MAX_LENGTH = 2;

const validateRequiredFields = (values: any, requiredFields: string[], basePath: string, ctx: z.RefinementCtx): void => {
  requiredFields.forEach((field) => {
    if (!values[field]) {
      ctx.addIssue({
        path: basePath ? [basePath, field] : [field],
        message: 'Required',
        code: 'custom',
      });
    }
  });
};

const GnssSchema = z.object({
  latitude: z.union([z.string(), z.number()]).optional().nullable(),
  longitude: z.union([z.string(), z.number()]).optional().nullable(),
});

const EconomicOperatorSchema = z.object({
  identificationNumber: z.string().optional().nullable(),
});

const AddressSchema = z.object({
  street: z.string().optional().nullable(),
  postcode: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
});

const PostcodeSchema = z.object({
  postcode: z.string().optional().nullable(),
  houseNumber: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
});

const WarehouseSchema = z.object({
  type: z.string(),
  identifier: z.string(),
});

export const GoodLocationListItemSchema = z.object({
  _id: z.any(),
  defaultLocation: z.boolean().optional().nullable(),
  type: z.nativeEnum(GoodsLocationType),
  qualifierOfIdentification: z.string(),
  unLocode: z.string(),
  unLocodeDescription: z.string().optional().nullable(),
  customsOffice: z
    .object({
      referenceNumber: z.string().optional().nullable(),
    })
    .optional(),
  gnss: GnssSchema.optional(),
  economicOperator: EconomicOperatorSchema.optional(),
  authorisationNumber: z.string().optional().nullable(),
  additionalIdentifier: z.string().optional().nullable(),
  address: AddressSchema.optional(),
  postcode: PostcodeSchema.optional(),
  warehouses: z.array(WarehouseSchema).optional(),
  virtuals: z
    .object({
      displayedCity: z.string().optional().nullable(),
      displayedPostcode: z.string().optional().nullable(),
    })
    .optional(),
});

export type GoodLocationListItem = z.infer<typeof GoodLocationListItemSchema>;

export const CreateGoodLocationSchema = z
  .object({
    defaultLocation: z.boolean().optional().nullable(),
    type: z.string().min(1, { message: 'Required' }),
    qualifierOfIdentification: z.string().min(1, { message: 'Required' }),
    unLocode: z.string().min(1, { message: 'Required' }),
    unLocodeDescription: z.string().optional().nullable(),
    customsOffice: z.object({
      referenceNumber: z.string().min(1, { message: 'Required' }),
    }),
    gnss: GnssSchema.optional().nullable(),
    economicOperator: EconomicOperatorSchema.optional().nullable(),
    authorisationNumber: z.string().optional().nullable(),
    additionalIdentifier: z.string().optional().nullable(),
    address: AddressSchema.optional().nullable(),
    postcode: PostcodeSchema.optional().nullable(),
    warehouses: z.array(WarehouseSchema).optional(),
  })
  .superRefine(({ qualifierOfIdentification, postcode, gnss, economicOperator, authorisationNumber, address }, ctx) => {
    switch (qualifierOfIdentification) {
      case 'T': {
        validateRequiredFields(postcode, ['postcode', 'country', 'houseNumber'], 'postcode', ctx);
        break;
      }
      case 'W': {
        validateRequiredFields(gnss, ['latitude', 'longitude'], 'gnss', ctx);
        break;
      }
      case 'X':
        validateRequiredFields(economicOperator, ['identificationNumber'], 'economicOperator', ctx);
        break;
      case 'Y':
        validateRequiredFields({ authorisationNumber }, ['authorisationNumber'], '', ctx);
        break;
      case 'Z': {
        validateRequiredFields(address, ['street', 'postcode', 'country', 'city'], 'address', ctx);

        if (address?.country && address.country.length > ADDRESS_COUNTRY_MAX_LENGTH) {
          ctx.addIssue({
            path: ['address', 'country'],
            message: 'Max 2 characters',
            code: 'custom',
          });
        }
        break;
      }
      default:
        break;
    }
  });

export type CreateGoodLocation = z.infer<typeof CreateGoodLocationSchema>;

export const UpdateGoodLocationSchema = CreateGoodLocationSchema.and(
  z.object({
    _id: z.string().min(1, { message: 'Required' }),
  }),
);

export type UpdateGoodLocation = z.infer<typeof UpdateGoodLocationSchema>;

export const RetrieveGoodLocationsSchema = z.object({
  criteria: z
    .object({
      filters: z.object({ searchQuery: z.string().optional() }).optional(),
      pagination: z.object({ page: z.number().optional(), size: z.number().optional() }).optional(),
      sorting: z.object({ field: z.string().optional(), direction: z.enum([SortDirection.ASC, SortDirection.DESC]).optional() }).optional(),
    })
    .optional(),
});

export const RetrieveGoodLocationsResponseSchema = z.object({
  data: z.object({
    goodLocations: z.array(GoodLocationListItemSchema),
    totalItems: z.number(),
  }),
});
